import { Box, Divider, Skeleton, tooltipClasses, Typography } from "@mui/material";
import { StyledCard } from "./components/StyledComponents";
import RedirectIcon from "../../Components/Icons/RedirectIcon";
import InfoIcon from "../../Components/Icons/InfoIcon";
import ChangeIndicator from "./components/ChangeIndicator";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import NonOperationalBreakdown from "./NonOperationalBreakdown";
import { Link } from "react-router-dom";
import { getInventoryFilterUrl } from "./CommonFunctions";
import { useTranslation } from "react-i18next";
import { useBaseEntityInfo } from "../../Hooks/usePagesContent";

const TotalVehiclesCard = ({ stats, globalFilters, loading }) => {
  const {
    total: totalCount,
    operational: operationalCount,
    non_operational: nonOperationalCount,
    operational_diff: operationalDiff,
    non_operational_diff: nonOperationalDiff,
  } = stats;

  const { baseEntityTypePluralLable } = useBaseEntityInfo();

  const operationalInventoryUrl = getInventoryFilterUrl(globalFilters, {
    tag_battery_status: ["Operational"],
  });
  const nonOperationalInventoryUrl = getInventoryFilterUrl(globalFilters, {
    tag_battery_status: ["Non-Operational"],
  });

  const operationalChange = operationalDiff;
  const nonOperationalChange = nonOperationalDiff;

  const calculatePercentage = (change, todayCount) => {
    if (change === 0) return 0;

    const yesterdayCount = todayCount - change;

    if (yesterdayCount === 0) {
      return Math.abs(todayCount) * 100;
    }

    const baseCount = Math.abs(yesterdayCount);

    return (Math.abs(change) / baseCount) * 100;
  };

  const operationalPct = calculatePercentage(operationalChange, operationalCount);
  const nonOperationalPct = calculatePercentage(nonOperationalChange, nonOperationalCount);

  return (
    <StyledCard>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="fw500s16lh20c000000">Total {baseEntityTypePluralLable}</Typography>
        {loading ? (
          <Skeleton variant="text" width={60} height={32} />
        ) : (
          <Typography variant="fw500s20c000000" sx={{ fontSize: "24px", lineHeight: "24px" }}>
            {totalCount ?? "-"}
          </Typography>
        )}
      </Box>
      <Divider flexItem sx={{ borderColor: "#E4E4E4", my: 1.5 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="fw400s14c000000" sx={{ lineHeight: "20px" }}>
              Operational
            </Typography>
            <PrimaryTooltip
              title="Vehicles in working condition that are available for deployment (Idle + Active + Charging) and have maintained connectivity within the last 24 hours"
              placement="right"
              sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                  maxWidth: "200px",
                  textAlign: "left !important",
                }
              }}
            >
              <InfoIcon
                sx={{ width: 14, height: 14, "&:hover ": { color: "#4D44D8" } }}
              />
            </PrimaryTooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loading ? (
              <Skeleton variant="text" width={40} height={24} />
            ) : (
              <>
                <Typography variant="fw500s16lh20c000000">{operationalCount ?? '-'}</Typography>
                <ChangeIndicator count={operationalChange} percentage={operationalPct} positiveWhenIncrease={true} />
              </>
            )}
          </Box>
          <Link
            to={operationalInventoryUrl}
            style={{ display: "contents", textDecoration: "none" }}
            rel="noopener noreferrer"
            target="_blank"
          >
            <PrimaryTooltip title="View in Inventory">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "#686868",
                  cursor: "pointer",
                  transition: "color 0.3s ease",
                  "&:hover": { color: "#4D44D8" },
                }}
              >
                <Typography
                  variant="fw500s12c686868"
                  sx={{ lineHeight: "18px", color: "inherit" }}
                >
                  Inventory
                </Typography>
                <RedirectIcon sx={{ fontSize: 12, color: "inherit" }} />
              </Box>
            </PrimaryTooltip>
          </Link>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="fw400s14c000000" sx={{ lineHeight: "20px" }}>
              Non-Operational
            </Typography>
            <PrimaryTooltip
              title="Vehicles that are out of service due to maintenance, repairs, or technical issues"
              placement="right"
              sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                  maxWidth: "200px",
                  textAlign: "left !important",
                }
              }}
            >
              <InfoIcon
                sx={{ width: 14, height: 14, "&:hover ": { color: "#4D44D8" } }}
              />
            </PrimaryTooltip>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loading ? (
              <Skeleton variant="text" width={40} height={24} />
            ) : (
              <>
                <Typography variant="fw500s16lh20c000000">{nonOperationalCount ?? '-'}</Typography>
                <ChangeIndicator count={nonOperationalChange} percentage={nonOperationalPct} positiveWhenIncrease={false} />
              </>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Link
              to={nonOperationalInventoryUrl}
              style={{ display: "contents", textDecoration: "none" }}
              rel="noopener noreferrer"
              target="_blank"
            >
              <PrimaryTooltip title="View in Inventory">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#686868",
                    cursor: "pointer",
                    transition: "color 0.3s ease",
                    "&:hover": { color: "#4D44D8" },
                  }}
                >
                  <Typography variant="fw500s12c686868" sx={{ lineHeight: "18px", color: "inherit" }}>
                    Inventory
                  </Typography>
                  <RedirectIcon sx={{ fontSize: 12, color: "inherit" }} />
                </Box>
              </PrimaryTooltip>
            </Link>
            <Box>
              <NonOperationalBreakdown globalFilters={globalFilters} />
            </Box>
          </Box>
        </Box>
      </Box>
    </StyledCard>
  );
};

export default TotalVehiclesCard;
